export const STYLES = `
:host {
  all: initial;
  --_va-accent: var(--va-accent, #6d28d9);
  --_va-accent-2: var(--va-accent-2, #8b5cf6);
  --_va-on-accent: var(--va-on-accent, #ffffff);
  --_va-surface: var(--va-surface, #ffffff);
  --_va-fg: var(--va-fg, #1a1a2e);
  --_va-muted-fg: var(--va-muted-fg, #6b7280);
  --_va-muted: var(--va-muted, #f3f4f6);
  --_va-muted-hover: #e5e7eb;
  --_va-border: var(--va-border, rgba(17, 24, 39, 0.08));
  --_va-error: var(--va-error, #ef4444);
  --_va-danger: var(--va-danger, #e11d48);
  /* Minimum touch target: 44px (iOS) also clears Android's 48dp at default DPR. */
  --_va-tap: 2.75rem;
  /* Fixed-placement offset = base gap + the device safe-area inset for that edge,
     so a bottom pill clears the home indicator and a notch never overlaps a top
     pill. env() resolves to 0px off mobile, leaving the plain 1.25rem gap. */
  --_va-gap: 1.25rem;
  --_va-inset-t: calc(var(--_va-gap) + env(safe-area-inset-top, 0px));
  --_va-inset-b: calc(var(--_va-gap) + env(safe-area-inset-bottom, 0px));
  --_va-inset-s: calc(var(--_va-gap) + env(safe-area-inset-left, 0px));
  --_va-inset-e: calc(var(--_va-gap) + env(safe-area-inset-right, 0px));
}

:host([data-theme="dark"]) {
  --_va-surface: var(--va-surface, #1e1e2e);
  --_va-fg: var(--va-fg, #e6e6f0);
  --_va-muted-fg: var(--va-muted-fg, #a1a1b5);
  --_va-muted: var(--va-muted, #2a2a3c);
  --_va-muted-hover: #3a3a50;
  --_va-border: var(--va-border, rgba(255, 255, 255, 0.10));
}

:host([data-placement="bottom-right"]) .va-shell { position: fixed; bottom: var(--_va-inset-b); inset-inline-end: var(--_va-inset-e); }
:host([data-placement="bottom-left"]) .va-shell { position: fixed; bottom: var(--_va-inset-b); inset-inline-start: var(--_va-inset-s); }
:host([data-placement="top-right"]) .va-shell { position: fixed; top: var(--_va-inset-t); inset-inline-end: var(--_va-inset-e); }
:host([data-placement="top-left"]) .va-shell { position: fixed; top: var(--_va-inset-t); inset-inline-start: var(--_va-inset-s); }
:host([data-placement="inline"]) .va-shell { position: static; }

.va-shell {
  font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
  color: var(--_va-fg);
  z-index: 2147483000;
  -webkit-font-smoothing: antialiased;
}

/* ----- shared button reset ----- */
.va-trigger, .va-call-button, .va-mute-button, .va-text-input button {
  cursor: pointer; border: 0; border-radius: 999px; font: inherit;
  transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, opacity 0.2s ease;
}
.va-trigger:active, .va-call-button:active { transform: scale(0.97); }
.va-trigger:focus-visible, .va-call-button:focus-visible,
.va-mute-button:focus-visible {
  outline: 2px solid var(--_va-accent); outline-offset: 2px;
}

.va-sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}

/* ----- collapsed trigger pill ----- */
.va-trigger {
  display: inline-flex; align-items: center; gap: 0.6rem;
  padding: 0.7rem 1.15rem;
  background: linear-gradient(135deg, var(--_va-accent), var(--_va-accent-2));
  color: var(--_va-on-accent);
  font-weight: 600; font-size: 0.95rem;
  box-shadow: 0 10px 24px rgba(109, 40, 217, 0.35);
}
.va-trigger:hover { transform: translateY(-1px); box-shadow: 0 14px 30px rgba(109, 40, 217, 0.42); }
:host([data-variant="full"]) .va-trigger { padding: 0.8rem 1.4rem; }
.va-trigger .va-orb,
.va-trigger .va-orb-canvas {
  width: 30px; height: 30px;
}
.va-trigger .va-orb-canvas { filter: none; }
.va-trigger .va-orb { flex: 0 0 auto; }
.va-trigger-label { min-width: 0; overflow-wrap: anywhere; text-align: start; }

/* ----- in-call folded pill: orb + hang-up + expand ----- */
.va-trigger-call {
  display: inline-flex; align-items: center; gap: 0.4rem;
  padding: 0.4rem 0.5rem;
  background: var(--_va-surface);
  border: 1px solid var(--_va-border);
  border-radius: 999px;
  box-shadow: 0 12px 30px rgba(17, 24, 39, 0.18), 0 2px 8px rgba(17, 24, 39, 0.06);
}
.va-trigger-orb, .va-trigger-expand {
  cursor: pointer; border: 0; font: inherit; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 999px;
  transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease;
}
.va-trigger-orb {
  width: 3rem; height: 3rem; background: transparent;
}
.va-trigger-orb .va-orb,
.va-trigger-orb .va-orb-canvas { width: 44px; height: 44px; }
.va-trigger-orb .va-orb-canvas { filter: none; }
.va-trigger-hangup {
  width: 3rem; height: 3rem; padding: 0; flex: 0 0 auto;
}
.va-trigger-hangup .va-call-icon { width: 1.5rem; height: 1.5rem; }
.va-trigger-expand {
  width: var(--_va-tap); height: var(--_va-tap);
  background: #1a1a2e; color: #ffffff;
}
.va-trigger-expand:hover { transform: translateY(-1px); }
.va-trigger-orb:focus-visible, .va-trigger-expand:focus-visible {
  outline: 2px solid var(--_va-accent); outline-offset: 2px;
}
.va-trigger-expand .va-call-icon { width: 1.15rem; height: 1.15rem; }

/* ----- panel / card ----- */
.va-panel {
  display: flex; flex-direction: column; align-items: stretch; gap: 0.9rem;
  min-width: 19rem; max-width: 22rem;
  padding: 1.1rem 1.15rem 1.25rem;
  border-radius: 1.25rem;
  background: var(--_va-surface);
  border: 1px solid var(--_va-border);
  box-shadow: 0 20px 50px rgba(17, 24, 39, 0.16), 0 2px 8px rgba(17, 24, 39, 0.06);
}
:host([data-variant="full"]) .va-panel { min-width: 23rem; }

/* bar: a horizontal, always-expanded docked control strip */
:host([data-variant="bar"]) .va-panel {
  flex-direction: row; align-items: center; gap: 0.9rem; border-radius: 999px;
  padding-block: 0.6rem; padding-inline: 1.1rem 0.75rem;
  min-width: 23rem;
  min-width: min(23rem, calc(100vw - 2rem));
  min-width: min(23rem, calc(100dvw - 2rem));
}
:host([data-variant="bar"]) .va-body { order: -1; }
:host([data-variant="bar"]) .va-header { flex: 1 1 auto; }
:host([data-variant="bar"]) .va-footer { flex: 0 0 auto; }
:host([data-variant="bar"][data-show-transcript="true"]) .va-panel,
:host([data-variant="bar"][data-text-input="true"]) .va-panel {
  flex-direction: column; align-items: stretch; border-radius: 1.25rem;
  padding: 1rem;
  /* This selector outweighs the generic viewport cap below, so its own min-width
     floor must stay viewport-aware or it overflows a narrow phone (min-width
     wins over max-width when they conflict). */
  min-width: 21rem;
  min-width: min(21rem, calc(100vw - 2rem));
  min-width: min(21rem, calc(100dvw - 2rem));
}

/* ----- header ----- */
.va-header { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
.va-header-status { display: inline-flex; align-items: center; gap: 0.65rem; min-width: 0; }
.va-header-actions { display: inline-flex; gap: 0.35rem; }
.va-status-label { font-weight: 600; font-size: 1.02rem; letter-spacing: -0.01em; min-width: 0; overflow-wrap: anywhere; }
:host([data-variant="bar"]) .va-status-label { font-size: 0.95rem; }

/* ----- body / orb ----- */
.va-body { display: flex; align-items: center; justify-content: center; padding: 0.5rem 0 0.25rem; }
:host([data-variant="bar"]) .va-body { padding: 0; }

.va-orb {
  display: inline-flex; align-items: center; justify-content: center;
  width: 132px; height: 132px;
}
.va-orb-canvas {
  display: block; width: 132px; height: 132px; border-radius: 50%;
  filter: drop-shadow(0 12px 30px var(--va-orb-shadow, rgba(109, 40, 217, 0.35)));
}
:host([data-variant="bar"]) .va-orb { width: 44px; height: 44px; }
:host([data-variant="bar"]) .va-orb-canvas { width: 44px; height: 44px; }

/* ----- transcript ----- */
.va-transcript {
  display: flex; flex-direction: column; gap: 0.45rem;
  min-height: 3rem; max-height: 10rem; overflow-y: auto;
  padding: 0.15rem 0.1rem; scrollbar-width: thin;
}
.va-transcript-message {
  box-sizing: border-box; max-width: 86%; padding: 0.55rem 0.7rem;
  border-radius: 0.75rem; font-size: 0.88rem; line-height: 1.35;
  overflow-wrap: anywhere; white-space: pre-wrap;
}
.va-transcript-message-agent {
  align-self: flex-start; background: var(--_va-muted); color: var(--_va-fg);
  border-end-start-radius: 0.2rem;
}
.va-transcript-message-user {
  align-self: flex-end; background: var(--_va-accent); color: var(--_va-on-accent);
  border-end-end-radius: 0.2rem;
}
.va-transcript-message[data-final="false"] { opacity: 0.76; }

/* ----- typed message input ----- */
.va-text-input {
  display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 0.45rem;
}
.va-text-input input {
  box-sizing: border-box; min-width: 0; height: var(--_va-tap); padding: 0 0.75rem;
  border: 1px solid var(--_va-border); border-radius: 0.65rem;
  background: var(--_va-surface); color: var(--_va-fg); font: inherit;
}
.va-text-input input::placeholder { color: var(--_va-muted-fg); }
.va-text-input input:focus-visible {
  outline: 2px solid var(--_va-accent); outline-offset: 1px;
}
.va-text-input button {
  min-width: 4.5rem; height: var(--_va-tap); padding: 0 0.85rem;
  border: 0; border-radius: 0.65rem; font-weight: 600;
  background: var(--_va-accent); color: var(--_va-on-accent);
}
.va-text-input button:disabled, .va-text-input input:disabled {
  cursor: not-allowed; opacity: 0.5;
}
@media (max-width: 24rem) {
  .va-text-input { grid-template-columns: minmax(0, 1fr); }
  .va-text-input button { width: 100%; }
}

/* ----- footer / call button ----- */
.va-footer { display: flex; align-items: center; gap: 0.5rem; }
:host(:not([data-variant="bar"])) .va-footer { justify-content: center; }
.va-call-button {
  display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem;
  min-height: var(--_va-tap);
  padding: 0.75rem 1.4rem; font-weight: 600; font-size: 0.95rem;
  background: linear-gradient(135deg, var(--_va-accent), var(--_va-accent-2));
  color: var(--_va-on-accent);
  box-shadow: 0 10px 24px rgba(109, 40, 217, 0.32);
}
:host(:not([data-variant="bar"])) .va-call-button { flex: 1 1 auto; }
.va-call-button:hover { transform: translateY(-1px); box-shadow: 0 14px 30px rgba(109, 40, 217, 0.4); }
.va-call-button[data-active="true"] {
  background: linear-gradient(135deg, var(--_va-danger), var(--_va-error));
  box-shadow: 0 10px 24px rgba(225, 29, 72, 0.32);
}
.va-call-button[data-active="true"]:hover { box-shadow: 0 14px 30px rgba(225, 29, 72, 0.4); }
.va-call-icon { width: 1.25rem; height: 1.25rem; flex: 0 0 auto; }
.va-footer-slot { flex: 0 0 auto; }

/* ----- mute / minimize / dismiss ----- */
.va-mute-button {
  display: inline-flex; align-items: center; justify-content: center;
  flex: 0 0 auto; width: var(--_va-tap); height: var(--_va-tap); font-size: 0.95rem; line-height: 1;
  background: var(--_va-muted); color: var(--_va-muted-fg);
}
.va-mute-button:hover { background: var(--_va-muted-hover); color: var(--_va-fg); }
:host([data-muted="true"]) .va-mute-button {
  background: linear-gradient(135deg, var(--_va-danger), var(--_va-error)); color: var(--_va-on-accent);
}

/* ----- collapse float: a bottom-right chevron-down that minimizes the panel ----- */
.va-shell { position: relative; }
.va-collapse-float {
  position: absolute; inset-inline-end: -0.5rem; bottom: -0.5rem;
  cursor: pointer; border: 0; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 2.75rem; height: 2.75rem; border-radius: 999px;
  background: var(--_va-surface); color: var(--_va-fg);
  box-shadow: 0 8px 22px rgba(17, 24, 39, 0.22), 0 2px 6px rgba(17, 24, 39, 0.08);
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}
.va-collapse-float:hover { transform: translateY(-1px); box-shadow: 0 12px 28px rgba(17, 24, 39, 0.28); }
.va-collapse-float:focus-visible { outline: 2px solid var(--_va-accent); outline-offset: 2px; }
.va-collapse-float .va-call-icon { width: 1.25rem; height: 1.25rem; }

/* ----- in-call: a larger, focused view ----- */
:host(:not([data-variant="bar"])[data-status="connecting"]) .va-panel,
:host(:not([data-variant="bar"])[data-status="connected"]) .va-panel {
  min-width: 21rem; padding: 1.35rem 1.4rem 1.6rem; gap: 1.15rem;
}
/* Only enlarge the orb inside the expanded panel — gated on data-expanded so a
   folded trigger pill mid-call (still data-status="connected") keeps its small orb. */
:host(:not([data-variant="bar"]):not([data-show-transcript="true"])[data-expanded="true"][data-status="connecting"]) .va-orb,
:host(:not([data-variant="bar"]):not([data-show-transcript="true"])[data-expanded="true"][data-status="connected"]) .va-orb,
:host(:not([data-variant="bar"]):not([data-show-transcript="true"])[data-expanded="true"][data-status="connecting"]) .va-orb-canvas,
:host(:not([data-variant="bar"]):not([data-show-transcript="true"])[data-expanded="true"][data-status="connected"]) .va-orb-canvas {
  width: 176px; height: 176px;
}
:host([data-show-transcript="true"]) .va-orb,
:host([data-show-transcript="true"]) .va-orb-canvas {
  width: 52px;
  height: 52px;
}
:host([data-show-transcript="true"]) .va-orb-canvas {
  filter: drop-shadow(0 6px 14px var(--va-orb-shadow, rgba(109, 40, 217, 0.28)));
}
:host([data-show-transcript="true"]) .va-transcript {
  min-height: 12rem;
  /* dvh tracks mobile Safari/Chrome's dynamic toolbar so the transcript never
     grows behind a shown address bar; vh is the pre-dvh fallback. */
  max-height: min(24rem, 52vh);
  max-height: min(24rem, 52dvh);
}
:host([data-show-transcript="true"]) .va-panel,
:host([data-text-input="true"]) .va-panel {
  box-sizing: border-box;
  width: min(22rem, calc(100vw - 2rem));
  width: min(22rem, calc(100dvw - 2rem));
  min-width: 0;
}
:host([data-placement="inline"][data-show-transcript="true"]) .va-panel,
:host([data-placement="inline"][data-text-input="true"]) .va-panel {
  width: 100%;
  min-width: 0;
}
:host([data-placement="inline"][data-show-transcript="true"]),
:host([data-placement="inline"][data-text-input="true"]) {
  display: block;
  width: 100%;
}

/* Fixed-placement panels must never exceed the viewport's inline size, regardless
   of the variant/state min-width floors above. max-width alone can't cap a larger
   min-width (CSS resolves min over max), so the min-width floor is also released
   here. Covers compact/full/bar and the in-call bump; inline placement flows in
   normal layout and is handled by the rules above. */
:host(:not([data-placement="inline"])) .va-panel,
:host(:not([data-placement="inline"])[data-variant="full"]) .va-panel,
:host(:not([data-placement="inline"]):not([data-variant="bar"])[data-status="connecting"]) .va-panel,
:host(:not([data-placement="inline"]):not([data-variant="bar"])[data-status="connected"]) .va-panel {
  box-sizing: border-box;
  max-width: calc(100vw - 2rem);
  max-width: calc(100dvw - 2rem);
  min-width: 0;
}

/* In a short viewport (landscape phone) an expanded panel can be taller than the
   screen. Cap it to the visible area minus the safe-area insets and let its body
   scroll, so no control is ever pushed off-screen. Fixed placement only —
   inline flows in the host's normal layout. */
:host(:not([data-placement="inline"])) .va-panel {
  max-height: calc(100vh - var(--_va-inset-t) - var(--_va-inset-b));
  max-height: calc(100dvh - var(--_va-inset-t) - var(--_va-inset-b));
  overflow-y: auto;
}

/* ----- animations ----- */
.va-status-label.va-shimmer { animation: va-shimmer 2.6s ease-in-out infinite; }
@keyframes va-shimmer { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }

:host([data-reduced-motion="true"]) * { animation: none !important; transition: none !important; }
`;
